import { cConst, cRange, defaultLayer, gradient, vConst, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Reference effect #2: hit spark, Smash-style (plan §"Reference effects").
 * Frame-1 flash → radial streaks → expanding shockwave ring → spinning debris
 * → soft smoke puff. Timing per design principle #5: flash frame 1, expansion
 * frames 2–6, linger, dissipate.
 */
export const hitSparkEffect = {
    version: 1,
    meta: { name: 'Hit Spark', duration: 0.8, loop: false, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'hit_flash',
            name: 'flash',
            delay: 0,
            duration: 0.05,
            maxParticles: 4,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.12),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(1.6),
                    rotation: vRange(0, 360),
                    color: cConst([1, 1, 1, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('pulse') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 1, 1]], [0.5, [1, 0.92, 0.5, 0.9]], [1, [1, 0.6, 0.15, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'star' } },
        }),
        defaultLayer({
            id: 'hit_streaks',
            name: 'radial streaks',
            delay: 0.016,
            duration: 0.05,
            maxParticles: 16,
            emitter: {
                mode: { type: 'burst', count: 9 },
                shape: { type: 'ring', radius: 0.05 },
                initial: {
                    lifetime: vRange(0.18, 0.3),
                    speed: vRange(6, 11),
                    spread: vConst(6),
                    size: vRange(0.35, 0.6),
                    rotation: vRange(0, 360),
                    color: cRange([1, 1, 0.75, 1], [1, 0.85, 0.4, 1]),
                },
            },
            modules: [
                { type: 'drag', enabled: true, coefficient: 9 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOutBack') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.7, [1, 0.9, 0.45, 1]], [1, [1, 0.5, 0.1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'streak' } },
        }),
        defaultLayer({
            id: 'hit_ring',
            name: 'shockwave ring',
            delay: 0.03,
            duration: 0.05,
            maxParticles: 4,
            emitter: {
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vConst(0.28),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vConst(2.4),
                    rotation: vConst(0),
                    color: cConst([1, 0.95, 0.7, 0.9]),
                },
            },
            modules: [
                // ring expands fast then eases out — the "size curve as shockwave" trick
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.5, [1, 0.9, 0.5, 0.6]], [1, [1, 0.7, 0.3, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'ring' } },
        }),
        defaultLayer({
            id: 'hit_debris',
            name: 'debris',
            delay: 0.03,
            duration: 0.05,
            maxParticles: 16,
            emitter: {
                mode: { type: 'burst', count: 7 },
                shape: { type: 'sphere', radius: 0.08, shell: true },
                initial: {
                    lifetime: vRange(0.35, 0.6),
                    speed: vRange(3, 6),
                    spread: vConst(25),
                    size: vRange(0.07, 0.16),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-720, 720),
                    color: cRange([1, 0.9, 0.55, 1], [1, 0.65, 0.25, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: 18 },
                { type: 'drag', enabled: true, coefficient: 3 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 1, 1]], [0.5, [1, 0.8, 0.4, 1]], [0.85, [0.9, 0.45, 0.15, 1]], [1, [0.6, 0.25, 0.1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'hardDot' } },
        }),
        defaultLayer({
            id: 'hit_smoke',
            name: 'smoke linger',
            delay: 0.08,
            duration: 0.15,
            maxParticles: 12,
            emitter: {
                mode: { type: 'burst', count: 4, cycles: 2, interval: 0.07 },
                shape: { type: 'sphere', radius: 0.25 },
                initial: {
                    lifetime: vRange(0.4, 0.7),
                    speed: vRange(0.4, 0.9),
                    spread: vConst(40),
                    size: vRange(0.5, 0.9),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-60, 60),
                    color: cConst([0.45, 0.42, 0.5, 0.35]),
                },
            },
            modules: [
                { type: 'drag', enabled: true, coefficient: 2 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.25, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'procedural', hardness: 0.1 } },
        }),
    ],
};
